import { notifySessionUnavailable } from './sessionState';

export const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

let inactivityTimer = null;
let isTimerActive = false;
let deadline = undefined;
let inFlight = 0;

// Allows mocking Date.now() in tests
let getNow = () => Date.now();

/**
 * Handles session timeout by notifying the session is unavailable.
 */
const handleInactivityTimeout = () => {
  isTimerActive = false;
  inactivityTimer = null;
  deadline = undefined;
  inFlight = 0;
  notifySessionUnavailable();
};

/**
 * Clears the current timer without affecting other state.
 */
const clearTimer = () => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }
  inactivityTimer = null;
};

/**
 * Schedules the timeout based on the current deadline.
 * Will not schedule if requests are in-flight or timer is inactive.
 */
const scheduleDeadline = () => {
  clearTimer();

  if (!isTimerActive || inFlight > 0 || deadline === undefined) {
    return;
  }

  const remaining = deadline - getNow();
  if (remaining <= 0) {
    handleInactivityTimeout();
    return;
  }

  inactivityTimer = setTimeout(handleInactivityTimeout, remaining);
};

/**
 * Starts the inactivity timer.
 * Should be called after valid login or authenticated application reload.
 */
export const startInactivityTimer = () => {
  clearTimer();
  isTimerActive = true;
  inFlight = 0;
  deadline = getNow() + INACTIVITY_TIMEOUT_MS;
  scheduleDeadline();
};

/**
 * Handles a BFF request starting. Pauses the timer while requests are in-flight
 * to prevent race conditions where a request starts just before the deadline
 * and completes after, causing state to be cleared despite a successful operation.
 */
export const handleRequestStarted = () => {
  if (!isTimerActive) {
    return;
  }
  inFlight += 1;
  clearTimer();
};

/**
 * Handles a completed BFF response (success or non-session error).
 * Resets the deadline and reschedules the timer.
 */
export const handleActivity = () => {
  if (!isTimerActive) {
    return;
  }
  if (inFlight > 0) {
    inFlight -= 1;
  }
  deadline = getNow() + INACTIVITY_TIMEOUT_MS;
  scheduleDeadline();
};

/**
 * Handles a failed BFF request (network error, no response).
 * Does not reset the deadline but reschedules the timer if no requests are in-flight.
 */
export const handleRequestFailed = () => {
  if (!isTimerActive) {
    return;
  }
  if (inFlight > 0) {
    inFlight -= 1;
  }
  scheduleDeadline();
};

/**
 * Resets the inactivity timer after a completed BFF response.
 * @deprecated Use handleActivity() instead for proper in-flight request handling.
 */
export const resetInactivityTimer = () => {
  handleActivity();
};

/**
 * Stops the inactivity timer.
 * Should be called during logout.
 */
export const stopInactivityTimer = () => {
  clearTimer();
  isTimerActive = false;
  deadline = undefined;
  inFlight = 0;
};

/**
 * Checks if the inactivity timer is currently active.
 * @returns {boolean}
 */
export const isInactivityTimerActive = () => isTimerActive;

/**
 * Gets the timeout duration in milliseconds (for testing).
 * @returns {number}
 */
export const getInactivityTimeoutMs = () => INACTIVITY_TIMEOUT_MS;

/**
 * Gets the current number of in-flight requests (for testing).
 * @returns {number}
 */
export const getInFlightCount = () => inFlight;

/**
 * Sets a custom time function (for testing).
 * @param {Function} fn - Function that returns current time in ms
 */
export const setTimeFunction = (fn) => {
  getNow = fn || (() => Date.now());
};
